import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useSocket } from './SocketContext';

// ── Context ────────────────────────────────────────────────────────────────────
const SessionManagerContext = createContext(null);

// ── Provider ───────────────────────────────────────────────────────────────────
export function SessionManagerProvider({ children }) {
  // sessions: { [patientCard]: { patientCard, patientName, patientPhoto, exercises, activeIdx,
  //                              elapsedSec, isPaused, isDinging, appointment } }
  const [sessions, setSessions] = useState({});
  const { socketRef } = useSocket() || {};

  // ── Single global ticker — increments elapsedSec for all running sessions ──
  // Also auto-pauses a session when the active exercise's duration is reached,
  // so the ding fires even when SessionIpadView is not mounted.
  useEffect(() => {
    const id = setInterval(() => {
      setSessions(prev => {
        const keys = Object.keys(prev);
        if (!keys.length) return prev;
        const running = keys.filter(k => !prev[k].isPaused && !prev[k].isDinging && !prev[k].waitingForResume);
        if (!running.length) return prev;
        const next = { ...prev };
        running.forEach(k => {
          const s = next[k];
          const activeEx = s.exercises?.[s.activeIdx];
          const dur = Number(activeEx?.duration) || 0;
          const newElapsed = s.elapsedSec + 1;
          if (
            dur > 0 &&
            newElapsed >= dur &&
            activeEx?.status !== 'Completed' &&
            activeEx?.status !== 'Skipped'
          ) {
            next[k] = { ...s, elapsedSec: dur, isPaused: true, isDinging: true, autoStoppedIdx: s.activeIdx, dingAt: s.dingAt ?? Date.now(), waitingForResume: true, pauseReason: 'timerComplete' };
          } else {
            next[k] = { ...s, elapsedSec: newElapsed };
          }
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Page Visibility: catch up elapsed time when tab/screen was hidden ───────
  // Browsers throttle setInterval when the page is hidden; this compensates.
  useEffect(() => {
    let hiddenAt = null;
    const onVis = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt !== null) {
        const elapsed = Math.round((Date.now() - hiddenAt) / 1000);
        hiddenAt = null;
        if (elapsed > 0) {
          setSessions(prev => {
            const keys = Object.keys(prev);
            if (!keys.length) return prev;
            const running = keys.filter(k => !prev[k].isPaused && !prev[k].isDinging && !prev[k].waitingForResume);
            if (!running.length) return prev;
            const next = { ...prev };
            running.forEach(k => {
              const s = next[k];
              const activeEx = s.exercises?.[s.activeIdx];
              const dur = Number(activeEx?.duration) || 0;
              const newElapsed = s.elapsedSec + elapsed;
              if (
                dur > 0 &&
                newElapsed >= dur &&
                activeEx?.status !== 'Completed' &&
                activeEx?.status !== 'Skipped'
              ) {
                next[k] = { ...s, elapsedSec: dur, isPaused: true, isDinging: true, autoStoppedIdx: s.activeIdx, dingAt: s.dingAt ?? Date.now(), waitingForResume: true, pauseReason: 'timerComplete' };
              } else {
                next[k] = { ...s, elapsedSec: newElapsed };
              }
            });
            return next;
          });
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // ── Register a new (or resume an existing) session ────────────────────────
  const register = useCallback((patientCard, init) => {
    setSessions(prev => ({
      ...prev,
      [patientCard]: {
        patientCard,
        patientName:      init.patientName      ?? patientCard,
        patientPhoto:     init.patientPhoto     ?? null,
        exercises:        init.exercises        ?? [],
        activeIdx:        init.activeIdx        ?? 0,
        elapsedSec:       init.elapsedSec       ?? 0,
        isPaused:         init.isPaused         ?? false,
        isDinging:        init.isDinging        ?? false,
        autoStoppedIdx:   init.autoStoppedIdx   ?? null,
        waitingForResume: init.waitingForResume ?? false,
        pauseReason:      init.pauseReason     ?? null,
        appointment:      init.appointment      ?? null,
      },
    }));
  }, []);

  // ── Patch any fields of a session ─────────────────────────────────────────
  const update = useCallback((patientCard, patch) => {
    setSessions(prev =>
      prev[patientCard]
        ? { ...prev, [patientCard]: { ...prev[patientCard], ...patch } }
        : prev
    );
  }, []);

  // ── Remove a completed / ended session ────────────────────────────────────
  const unregister = useCallback((patientCard) => {
    setSessions(prev => {
      if (!prev[patientCard]) return prev;
      const { [patientCard]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // ── Clear the ding vibration on a card ────────────────────────────────────
  const acknowledgeCard = useCallback((patientCard) => {
    update(patientCard, { isDinging: false });
  }, [update]);

  // ── Socket integration: receive cross-device session events ───────────────
  // When another device starts, updates, or ends a session, we mirror that state
  // here so this device's SessionTabStrip and GlobalDingPlayer stay in sync.
  const _register = useRef(register);
  const _update   = useRef(update);
  const _unregister = useRef(unregister);
  _register.current   = register;
  _update.current     = update;
  _unregister.current = unregister;

  useEffect(() => {
    const sock = socketRef?.current;
    if (!sock) return;

    // All live sessions for this therapist — sent on connect / reconnect
    const onInit = (sessions) => {
      sessions.forEach(s => {
        _register.current(s.patientCard, {
          patientName:      s.patientName,
          patientPhoto:     s.patientPhoto,
          exercises:        s.exercises,
          activeIdx:        s.activeIdx,
          elapsedSec:       s.elapsedSec,
          isPaused:         s.isPaused,
          isDinging:        s.isDinging,
          autoStoppedIdx:   s.autoStoppedIdx,
          waitingForResume: s.waitingForResume,
          pauseReason:      s.pauseReason,
          appointment:      s.appointment,
        });
      });
    };

    // Another device just started a session
    const onRegistered = (s) => {
      _register.current(s.patientCard, {
        patientName:      s.patientName,
        patientPhoto:     s.patientPhoto,
        exercises:        s.exercises,
        activeIdx:        s.activeIdx,
        elapsedSec:       s.elapsedSec,
        isPaused:         s.isPaused,
        isDinging:        s.isDinging,
        autoStoppedIdx:   s.autoStoppedIdx,
        waitingForResume: s.waitingForResume,
        pauseReason:      s.pauseReason,
        appointment:      s.appointment,
      });
    };

    // Another device sent a state patch (exercise done, pause, periodic sync)
    const onUpdated = (s) => {
      _update.current(s.patientCard, {
        exercises:        s.exercises,
        activeIdx:        s.activeIdx,
        elapsedSec:       s.elapsedSec,
        isPaused:         s.isPaused,
        isDinging:        s.isDinging,
        autoStoppedIdx:   s.autoStoppedIdx,
        waitingForResume: s.waitingForResume,
        pauseReason:      s.pauseReason,
        ...(s.finishedAt ? { finishedAt: s.finishedAt } : {}),
      });
    };

    // Another device ended the session
    const onEnded = ({ patientCard }) => {
      _unregister.current(patientCard);
    };

    sock.on('session:init',       onInit);
    sock.on('session:registered', onRegistered);
    sock.on('session:updated',    onUpdated);
    sock.on('session:ended',      onEnded);

    return () => {
      sock.off('session:init',       onInit);
      sock.off('session:registered', onRegistered);
      sock.off('session:updated',    onUpdated);
      sock.off('session:ended',      onEnded);
    };
  }, [socketRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SessionManagerContext.Provider value={{
      sessions,
      sessionList: Object.values(sessions),
      register,
      update,
      unregister,
      acknowledgeCard,
    }}>
      {children}
    </SessionManagerContext.Provider>
  );
}

export const useSessionManager = () => useContext(SessionManagerContext);
